import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, Alert, Badge, Table, Nav } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon.jsx';
import { AlertTriangle, CalendarDays, Info, Rocket, Trophy, UserCircle, Users } from 'lucide-react';
import Loader from '../components/Loader';
import UpcomingDraft from '../components/UpcomingDraft';
import TeamStandingsChart from '../components/TeamStandingsChart';
import LeagueStandings from '../components/LeagueStandings.jsx';
import WeeklyMatchups from '../components/WeeklyMatchups.jsx';
import {
  useFetchAthleteByUserIdQuery,
} from '../slices/athleteApiSlice';
import {
  useFetchLeaguesQuery,
  useFetchAvailableLeaguesQuery,
  useAddLeagueMutation,
  useJoinLeagueMutation,
} from '../slices/leaguesApiSlice';
import {
  useGetAllDraftsQuery,
  useGetDraftByLeagueQuery,
} from '../slices/draftApiSlice';
import { useGetLeagueSeasonsQuery, useGetSeasonLeaderboardQuery } from '../slices/seasonsApiSlice';
import { useGetTeamLeaderboardQuery, useGetTeamsByLeagueQuery } from '../slices/teamApiSlice';
import { setStoredActiveLeagueId, useActiveLeagueId } from '../utils/activeLeague';
import './LeagueScreen.css';

const formatDate = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatMonthDay = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
  });
};

const buildWeekDateRanges = (seasonStartDate, totalWeeks) => {
  const startDate = seasonStartDate ? new Date(seasonStartDate) : null;
  const resolvedWeeks = Number(totalWeeks);

  if (!startDate || Number.isNaN(startDate.getTime()) || !Number.isFinite(resolvedWeeks) || resolvedWeeks <= 0) {
    return {};
  }

  return Array.from({ length: resolvedWeeks }, (_, index) => {
    const weekNumber = index + 1;
    const weekStart = new Date(startDate);
    weekStart.setDate(startDate.getDate() + (index * 7));

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    return [weekNumber, {
      startLabel: formatMonthDay(weekStart),
      endLabel: formatMonthDay(weekEnd),
    }];
  }).reduce((accumulator, [weekNumber, labels]) => {
    accumulator[weekNumber] = labels;
    return accumulator;
  }, {});
};

const isPastLeague = (league) => {
  const seasonSummary = league?.seasonSummary;
  const latestSeasonStatus = seasonSummary?.latestSeason?.status;

  return Boolean(
    latestSeasonStatus === 'completed' &&
    !seasonSummary?.hasActiveSeason &&
    !seasonSummary?.hasUpcomingSeason
  );
};

const getLeagueAthleteDisplayName = (athlete) => {
  const fullName = [athlete?.firstName, athlete?.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return athlete?.user?.username || athlete?.user?.email || 'Unnamed athlete';
};

const compareNullableRanks = (leftRank, rightRank, direction) => {
  if (leftRank == null && rightRank == null) return 0;
  if (leftRank == null) return 1;
  if (rightRank == null) return -1;
  return direction === 'asc' ? leftRank - rightRank : rightRank - leftRank;
};

const getResolvedTeamId = (teamLike) => {
  const rawId = teamLike?._id || teamLike?.id || teamLike?.teamId;
  if (!rawId) return null;
  return typeof rawId === 'object' ? String(rawId._id || rawId.id || rawId) : String(rawId);
};

const getResolvedAdminId = (teamLike) => {
  const rawId = teamLike?.admin?._id || teamLike?.admin;
  if (!rawId) return null;
  return typeof rawId === 'object' ? String(rawId._id || rawId.id || rawId) : String(rawId);
};

const DIVISION_NAMES = ['East', 'West', 'North', 'South'];

const normalizeTeamDivision = (division) => {
  if (DIVISION_NAMES.includes(division)) {
    return division;
  }

  return null;
};

const getCompactTeamName = (name) => {
  const normalizedName = String(name || '').trim();
  if (!normalizedName) return 'Unnamed team';

  const ownerStyleName = normalizedName.replace(/\s*'?s\s+team$/i, '').trim();
  if (ownerStyleName !== normalizedName) {
    return ownerStyleName;
  }

  const genericSuffixName = normalizedName.replace(/\s+team$/i, '').trim();
  return genericSuffixName || normalizedName;
};

const formatRecord = (wins = 0, losses = 0, ties = 0) => `${wins}-${losses}${ties ? `-${ties}` : ''}`;

const getSortedWeekNumbers = (weeksByKey = {}) => Object.keys(weeksByKey)
  .map((weekNumber) => Number(weekNumber))
  .filter((weekNumber) => Number.isFinite(weekNumber) && weekNumber > 0)
  .sort((left, right) => left - right);

const LeaguesScreen = () => {
  const navigate = useNavigate();
  const { id: routeLeagueId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo } = useSelector((state) => state.auth);
  const athleteId = userInfo?.athleteId;
  const userId = userInfo?._id;
  const storedActiveLeagueId = useActiveLeagueId();
  const availableLeaguesRef = useRef(null);
  const createLeagueRef = useRef(null);
  const requestedLeagueId = routeLeagueId || storedActiveLeagueId || undefined;

  const { data: athlete, isLoading: isLoadingAthlete, error: athleteError } = useFetchAthleteByUserIdQuery(requestedLeagueId ? {
    userId,
    leagueId: requestedLeagueId,
  } : userId, {
    skip: !userId,
  });
  const effectiveAthleteId = athleteId || athlete?._id;

  const leagueId = routeLeagueId || storedActiveLeagueId || athlete?.league?._id || athlete?.league;
  const { data: userLeague, isLoading: isLoadingUserLeague } = useFetchLeaguesQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: allLeagues, isLoading: isLoadingAllLeagues } = useFetchAvailableLeaguesQuery(userId, {
    skip: !userId,
  });
  const { data: existingDraft } = useGetDraftByLeagueQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: allDrafts } = useGetAllDraftsQuery(undefined, { skip: !userId });
  const { data: leagueSeasons } = useGetLeagueSeasonsQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: leagueTeams, isLoading: isLoadingLeagueTeams } = useGetTeamsByLeagueQuery(leagueId, {
    skip: !leagueId,
  });
  const { data: teamLeaderboardData, error: teamLeaderboardError } = useGetTeamLeaderboardQuery(leagueId, {
    skip: !leagueId,
  });

  const [addLeague, { isLoading: isAdding }] = useAddLeagueMutation();
  const [joinLeague] = useJoinLeagueMutation();

  const [leagueName, setLeagueName] = useState('');
  const [leagueDescription, setLeagueDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [showCreateLeagueForm, setShowCreateLeagueForm] = useState(false);
  const [athleteStandingsSort, setAthleteStandingsSort] = useState({ key: 'rank', direction: 'asc' });
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [selectedLeagueTab, setSelectedLeagueTab] = useState('standings');
  const [selectedStandingsTab, setSelectedStandingsTab] = useState('division');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const navIntent = searchParams.get('intent');
  const privateJoinToken = searchParams.get('joinToken') || '';

  const joinedLeagueIds = useMemo(() => new Set(
    (allLeagues || [])
      .filter((league) => Array.isArray(league?.athletes) && effectiveAthleteId && league.athletes.some((entry) => String(entry?._id || entry) === String(effectiveAthleteId)))
      .map((league) => String(league._id))
  ), [allLeagues, effectiveAthleteId]);

  const pastMemberLeagues = useMemo(() => (allLeagues || []).filter((league) => {
    const isMemberLeague = joinedLeagueIds.has(String(league?._id));
    const isManagedLeague = String(league?.admin?._id || league?.admin) === String(effectiveAthleteId);

    return (isMemberLeague || isManagedLeague) && isPastLeague(league);
  }), [allLeagues, effectiveAthleteId, joinedLeagueIds]);

  const visibleAvailableLeagues = useMemo(() => (allLeagues || []).filter((league) => !pastMemberLeagues.some((pastLeague) => String(pastLeague._id) === String(league._id))), [allLeagues, pastMemberLeagues]);

  useEffect(() => {
    if (athleteError) {
      toast.error('Failed to fetch athlete data');
    }
  }, [athleteError]);

  useEffect(() => {
    if (userInfo && effectiveAthleteId && !userInfo.athleteId) {
      const updatedUserInfo = { ...userInfo, athleteId: effectiveAthleteId };
      localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
    }
  }, [effectiveAthleteId, userInfo]);

  useEffect(() => {
    if (leagueId) {
      setStoredActiveLeagueId(leagueId);
    }
  }, [leagueId]);

  useEffect(() => {
    if (!navIntent) {
      return;
    }

    if (navIntent === 'create-league') {
      setShowCreateLeagueForm(true);
    }

    const targetRef = navIntent === 'create-league' ? createLeagueRef : availableLeaguesRef;
    targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('intent');
    setSearchParams(nextParams, { replace: true });
  }, [leagueId, navIntent, searchParams, setSearchParams]);

  const draftsByLeague = useMemo(() => {
    const map = {};
    if (Array.isArray(allDrafts)) {
      for (const draft of allDrafts) {
        const draftLeagueId = typeof draft.league === 'object' ? draft.league?._id : draft.league;
        if (draftLeagueId) {
          map[draftLeagueId] = draft;
        }
      }
    }
    return map;
  }, [allDrafts]);

  const noActiveSeasonForLeaderboard = teamLeaderboardError?.status === 404;
  const isHeadToHeadMode = userLeague?.scoringMode === 'head_to_head';
  const sortedMatchupWeekNumbers = useMemo(
    () => getSortedWeekNumbers(userLeague?.matchupsByWeek || {}),
    [userLeague?.matchupsByWeek]
  );

  const featuredMatchupWeekNumber = useMemo(() => {
    if (!isHeadToHeadMode) {
      return null;
    }

    const currentWeek = Number(userLeague?.currentMatchupWeekNumber);
    if (Number.isFinite(currentWeek) && currentWeek > 0) {
      return currentWeek;
    }

    const completedWeek = [...sortedMatchupWeekNumbers]
      .reverse()
      .find((weekNumber) => (
        (userLeague?.matchupsByWeek?.[weekNumber] || []).some(
          (matchup) => typeof matchup?.homePoints === 'number' && typeof matchup?.awayPoints === 'number'
        )
      ));

    return completedWeek || sortedMatchupWeekNumbers[0] || null;
  }, [isHeadToHeadMode, sortedMatchupWeekNumbers, userLeague?.currentMatchupWeekNumber, userLeague?.matchupsByWeek]);

  const headToHeadStandingsByTeamId = useMemo(() => {
    const standings = Array.isArray(userLeague?.headToHeadStandings) ? userLeague.headToHeadStandings : [];
    return new Map(
      standings
        .map((standing) => {
          const teamId = String(standing?.teamId || standing?.id || '');
          return teamId ? [teamId, standing] : null;
        })
        .filter(Boolean)
    );
  }, [userLeague?.headToHeadStandings]);

  const headToHeadWeeklySeriesByTeamId = useMemo(() => {
    const seriesByTeamId = new Map();

    if (!isHeadToHeadMode) {
      return seriesByTeamId;
    }

    const ensureTeamSeries = (teamId) => {
      if (!seriesByTeamId.has(teamId)) {
        seriesByTeamId.set(teamId, {
          weeklyPoints: [],
          weeklyOwnerBonusPoints: [],
        });
      }

      return seriesByTeamId.get(teamId);
    };

    for (const weekNumber of sortedMatchupWeekNumbers) {
      const weekMatchups = Array.isArray(userLeague?.matchupsByWeek?.[weekNumber])
        ? userLeague.matchupsByWeek[weekNumber]
        : [];

      for (const matchup of weekMatchups) {
        const homeTeamId = String(matchup?.homeTeamId || '');
        const awayTeamId = String(matchup?.awayTeamId || '');

        if (homeTeamId && typeof matchup?.homePoints === 'number') {
          const homeSeries = ensureTeamSeries(homeTeamId);
          homeSeries.weeklyPoints.push({ weekNumber, points: Number(matchup.homePoints) });
          homeSeries.weeklyOwnerBonusPoints.push({ weekNumber, points: Number(matchup.homeOwnerBonusPoints || 0) });
        }

        if (awayTeamId && typeof matchup?.awayPoints === 'number') {
          const awaySeries = ensureTeamSeries(awayTeamId);
          awaySeries.weeklyPoints.push({ weekNumber, points: Number(matchup.awayPoints) });
          awaySeries.weeklyOwnerBonusPoints.push({ weekNumber, points: Number(matchup.awayOwnerBonusPoints || 0) });
        }
      }
    }

    return seriesByTeamId;
  }, [isHeadToHeadMode, sortedMatchupWeekNumbers, userLeague?.matchupsByWeek]);

  const featuredMatchupByTeamId = useMemo(() => {
    const matchupsByTeamId = new Map();

    if (!isHeadToHeadMode || !featuredMatchupWeekNumber) {
      return matchupsByTeamId;
    }

    const featuredWeekMatchups = Array.isArray(userLeague?.matchupsByWeek?.[featuredMatchupWeekNumber])
      ? userLeague.matchupsByWeek[featuredMatchupWeekNumber]
      : [];

    for (const matchup of featuredWeekMatchups) {
      const homeTeamId = String(matchup?.homeTeamId || '');
      const awayTeamId = String(matchup?.awayTeamId || '');
      const homeName = matchup?.homeName || 'TBD';
      const awayName = matchup?.awayName || 'TBD';
      const hasScore = typeof matchup?.homePoints === 'number' && typeof matchup?.awayPoints === 'number';

      if (homeTeamId) {
        matchupsByTeamId.set(homeTeamId, {
          weekNumber: featuredMatchupWeekNumber,
          opponentTeamId: awayTeamId || null,
          opponentName: awayName,
          points: hasScore ? Number(matchup.homePoints) : null,
          opponentPoints: hasScore ? Number(matchup.awayPoints) : null,
          baseRosterPoints: hasScore ? Number(matchup.homeBasePoints || 0) : null,
          ownerBonusPoints: hasScore ? Number(matchup.homeOwnerBonusPoints || 0) : null,
          result: hasScore
            ? (matchup.homePoints === matchup.awayPoints ? 'T' : matchup.homePoints > matchup.awayPoints ? 'W' : 'L')
            : null,
          isFinal: Boolean(matchup?.isFinal),
          hasScore,
        });
      }

      if (awayTeamId) {
        matchupsByTeamId.set(awayTeamId, {
          weekNumber: featuredMatchupWeekNumber,
          opponentTeamId: homeTeamId || null,
          opponentName: homeName,
          points: hasScore ? Number(matchup.awayPoints) : null,
          opponentPoints: hasScore ? Number(matchup.homePoints) : null,
          baseRosterPoints: hasScore ? Number(matchup.awayBasePoints || 0) : null,
          ownerBonusPoints: hasScore ? Number(matchup.awayOwnerBonusPoints || 0) : null,
          result: hasScore
            ? (matchup.homePoints === matchup.awayPoints ? 'T' : matchup.awayPoints > matchup.homePoints ? 'W' : 'L')
            : null,
          isFinal: Boolean(matchup?.isFinal),
          hasScore,
        });
      }
    }

    return matchupsByTeamId;
  }, [featuredMatchupWeekNumber, isHeadToHeadMode, userLeague?.matchupsByWeek]);

  const leagueTeamCards = useMemo(() => {
    const baseTeams = Array.isArray(leagueTeams) ? leagueTeams : [];
    const leaderboardEntries = teamLeaderboardData?.leaderboard || [];
    const mergedTeamsById = new Map();
    const leaderboardByTeamId = new Map(
      leaderboardEntries
        .map((entry) => {
          const resolvedTeamId = getResolvedTeamId(entry);
          return resolvedTeamId ? [resolvedTeamId, entry] : null;
        })
        .filter(Boolean)
    );

    for (const team of baseTeams) {
      const resolvedTeamId = getResolvedTeamId(team);
      if (!resolvedTeamId) continue;

      mergedTeamsById.set(resolvedTeamId, {
        ...team,
        _id: resolvedTeamId,
      });
    }

    for (const entry of leaderboardEntries) {
      const resolvedTeamId = getResolvedTeamId(entry);
      if (!resolvedTeamId) continue;

      const existingTeam = mergedTeamsById.get(resolvedTeamId) || {};
      mergedTeamsById.set(resolvedTeamId, {
        ...existingTeam,
        ...entry,
        _id: resolvedTeamId,
        name: existingTeam.name || entry.teamName || 'Unnamed team',
      });
    }

    return Array.from(mergedTeamsById.values())
      .map((team) => {
        const resolvedTeamId = getResolvedTeamId(team);
        const leaderboardEntry = leaderboardByTeamId.get(resolvedTeamId);
        const fallbackAthletes = Array.isArray(team.athlete)
          ? team.athlete.map((athlete) => ({
              id: athlete._id,
              name: `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim() || 'Unnamed athlete',
              points: 0,
            }))
          : [];

        return {
          ...team,
          _id: resolvedTeamId,
          rank: isHeadToHeadMode
            ? Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.rank || 0) || null
            : leaderboardEntry?.rank || null,
          teamScore: isHeadToHeadMode
            ? Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.pointsFor || 0)
            : Number(leaderboardEntry?.totalPoints || 0),
          baseRosterPoints: isHeadToHeadMode
            ? Number(featuredMatchupByTeamId.get(resolvedTeamId)?.baseRosterPoints || 0)
            : Number(leaderboardEntry?.baseRosterPoints || team.baseRosterPoints || 0),
          weeklyMileage: leaderboardEntry?.weeklyMileage || [],
          weeklyPoints: headToHeadWeeklySeriesByTeamId.get(resolvedTeamId)?.weeklyPoints || [],
          ownerBonusMileage: leaderboardEntry?.ownerBonusMileage || [],
          weeklyOwnerBonusPoints: headToHeadWeeklySeriesByTeamId.get(resolvedTeamId)?.weeklyOwnerBonusPoints || [],
          ownerPointsMultiplier: Number(leaderboardEntry?.ownerPointsMultiplier || team.ownerPointsMultiplier || 1),
          ownerBonusPoints: isHeadToHeadMode
            ? Number(featuredMatchupByTeamId.get(resolvedTeamId)?.ownerBonusPoints || 0)
            : Number(leaderboardEntry?.ownerBonusPoints || team.ownerBonusPoints || 0),
          wins: Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.wins || 0),
          losses: Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.losses || 0),
          ties: Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.ties || 0),
          pointsFor: Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.pointsFor || 0),
          pointsAgainst: Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.pointsAgainst || 0),
          weeksPlayed: Number(headToHeadStandingsByTeamId.get(resolvedTeamId)?.weeksPlayed || 0),
          currentMatchup: featuredMatchupByTeamId.get(resolvedTeamId) || null,
          athletes: leaderboardEntry?.athletes || fallbackAthletes,
        };
      })
      .sort((left, right) => {
        if (isHeadToHeadMode) {
          if (left.rank && right.rank) {
            return left.rank - right.rank;
          }

          if ((right.wins || 0) !== (left.wins || 0)) {
            return (right.wins || 0) - (left.wins || 0);
          }

          if ((left.losses || 0) !== (right.losses || 0)) {
            return (left.losses || 0) - (right.losses || 0);
          }

          if ((right.pointsFor || 0) !== (left.pointsFor || 0)) {
            return (right.pointsFor || 0) - (left.pointsFor || 0);
          }

          return left.name.localeCompare(right.name);
        }

        if (left.rank && right.rank) {
          return left.rank - right.rank;
        }

        if ((right.teamScore || 0) !== (left.teamScore || 0)) {
          return (right.teamScore || 0) - (left.teamScore || 0);
        }

        return left.name.localeCompare(right.name);
      });
  }, [featuredMatchupByTeamId, headToHeadStandingsByTeamId, headToHeadWeeklySeriesByTeamId, isHeadToHeadMode, leagueTeams, teamLeaderboardData]);

  const divisionStandings = useMemo(() => {
    const divisionNames = leagueTeamCards.length < 13
      ? DIVISION_NAMES.slice(0, 2)
      : DIVISION_NAMES;
    const buildDivisionRows = (teams, divisionName) => teams.map((team, index) => ({
      id: team._id,
      name: getCompactTeamName(team.name),
      rank: index + 1,
      basePoints: Number(team.baseRosterPoints || 0),
      multiplier: Number(team.ownerPointsMultiplier || 1),
      total: Number(team.teamScore || (Number(team.baseRosterPoints || 0) + Number(team.ownerBonusPoints || 0))),
      division: divisionName,
    }));

    const sortDivisionTeams = (teams) => [...teams].sort((left, right) => {
      if (isHeadToHeadMode) {
        if ((right.wins || 0) !== (left.wins || 0)) {
          return (right.wins || 0) - (left.wins || 0);
        }

        if ((left.losses || 0) !== (right.losses || 0)) {
          return (left.losses || 0) - (right.losses || 0);
        }

        if ((right.pointsFor || 0) !== (left.pointsFor || 0)) {
          return (right.pointsFor || 0) - (left.pointsFor || 0);
        }

        return left.name.localeCompare(right.name);
      }

      if ((right.teamScore || 0) !== (left.teamScore || 0)) {
        return (right.teamScore || 0) - (left.teamScore || 0);
      }

      return left.name.localeCompare(right.name);
    });

    const groupedTeams = Object.fromEntries(divisionNames.map((division) => [division, []]));
    const sortedTeams = sortDivisionTeams(leagueTeamCards);

    sortedTeams.forEach((team) => {
      const division = normalizeTeamDivision(team.division);
      if (!division || !groupedTeams[division]) {
        return;
      }

      groupedTeams[division].push(team);
    });

    return divisionNames.map((divisionName) => ({
      key: divisionName.toLowerCase(),
      label: divisionName,
      teams: buildDivisionRows(sortDivisionTeams(groupedTeams[divisionName]), divisionName),
    }));
  }, [isHeadToHeadMode, leagueTeamCards]);

  const teamsById = useMemo(
    () => Object.fromEntries(leagueTeamCards.map((team) => [String(team._id), team])),
    [leagueTeamCards]
  );
  const currentLeagueUserTeam = useMemo(() => {
    const activeTeamId = getResolvedTeamId(athlete?.team);
    const activeTeamLeagueId = athlete?.team?.league?._id || athlete?.team?.league;
    if (activeTeamId && String(activeTeamLeagueId || '') === String(leagueId || '')) {
      return leagueTeamCards.find((team) => String(team._id) === activeTeamId) || athlete.team;
    }

    return leagueTeamCards.find((team) => String(getResolvedAdminId(team) || '') === String(userId || '')) || null;
  }, [athlete?.team, leagueId, leagueTeamCards, userId]);
  const selectedLeagueTeam = useMemo(
    () => leagueTeamCards.find((team) => String(team._id) === String(selectedTeamId)) || null,
    [leagueTeamCards, selectedTeamId]
  );

  const athleteStandingsRows = useMemo(() => {
    const athletes = Array.isArray(userLeague?.athletes) ? userLeague.athletes : [];

    return athletes
      .map((leagueAthlete) => ({
        id: leagueAthlete._id,
        name: getLeagueAthleteDisplayName(leagueAthlete),
        rank: leagueAthlete.salaryRank || null,
        seasonMiles: Number(leagueAthlete.seasonTotalMiles || leagueAthlete.multiplierSummary?.totalMiles || 0),
        currentWeekMiles: Number(leagueAthlete.currentWeekMiles || 0),
        currentWeekNumber: leagueAthlete.currentWeekNumber || null,
        multiplier: Number(leagueAthlete.multiplierSummary?.currentMultiplier || 1),
        multiplierCap: Number(leagueAthlete.multiplierSummary?.multiplierCap || 1),
      }));
  }, [userLeague?.athletes]);

  const currentLeagueWeekNumber = athleteStandingsRows.find((row) => row.currentWeekNumber != null)?.currentWeekNumber ?? null;

  const sortedAthleteStandingsRows = useMemo(() => {
    const rows = [...athleteStandingsRows];
    const { key, direction } = athleteStandingsSort;

    rows.sort((left, right) => {
      if (key === 'rank') {
        const rankComparison = compareNullableRanks(left.rank, right.rank, direction);
        if (rankComparison !== 0) return rankComparison;
        return left.name.localeCompare(right.name);
      }

      if (key === 'name') {
        return direction === 'asc'
          ? left.name.localeCompare(right.name)
          : right.name.localeCompare(left.name);
      }

      const leftValue = Number(left[key] || 0);
      const rightValue = Number(right[key] || 0);
      if (leftValue !== rightValue) {
        return direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
      }

      return left.name.localeCompare(right.name);
    });

    return rows;
  }, [athleteStandingsRows, athleteStandingsSort]);

  const handleAthleteStandingsSort = (key) => {
    setAthleteStandingsSort((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIndicator = (key) => {
    if (athleteStandingsSort.key !== key) return '';
    return athleteStandingsSort.direction === 'asc' ? ' ▲' : ' ▼';
  };

  useEffect(() => {
    if (!leagueTeamCards.length) {
      if (selectedTeamId) {
        setSelectedTeamId('');
      }
      return;
    }

    const hasSelectedTeam = leagueTeamCards.some((team) => String(team._id) === String(selectedTeamId));
    if (hasSelectedTeam) {
      return;
    }

    const defaultTeamId = currentLeagueUserTeam?._id || leagueTeamCards[0]?._id || '';
    setSelectedTeamId(defaultTeamId ? String(defaultTeamId) : '');
  }, [currentLeagueUserTeam?._id, leagueTeamCards, selectedTeamId]);

  useEffect(() => {
    setSelectedLeagueTab(isHeadToHeadMode ? 'matchups' : 'standings');
  }, [isHeadToHeadMode, leagueId]);

  useEffect(() => {
    setSelectedStandingsTab('division');
  }, [leagueId]);

  const renderSelectedTeamCard = (team) => {
    if (!team) {
      return (
        <div className="text-center py-4">
          <p className="text-secondary mb-0">No team is selected.</p>
        </div>
      );
    }

    return (
      <div className="league-team-card">
        <div className="league-team-card-top">
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
              <h4 className="text-primary mb-0">{team.name}</h4>
              {team.rank ? <Badge bg="secondary">#{team.rank}</Badge> : null}
              {team.division ? <Badge bg="light" text="dark">{team.division}</Badge> : null}
            </div>
            <small className="text-secondary">
              {isHeadToHeadMode
                ? team.currentMatchup
                  ? `Week ${team.currentMatchup.weekNumber} vs ${team.currentMatchup.opponentName}`
                  : `Record ${formatRecord(team.wins, team.losses, team.ties)}`
                : `${Number(team.teamScore || 0).toFixed(1)} points`}
            </small>
          </div>
          <button className="btn btn-sm btn-outline-modern" onClick={() => navigate(`/team/${team._id}`)}>
            View Team
          </button>
        </div>

        <div className="league-team-score-row">
          {isHeadToHeadMode ? (
            <>
              <div className="league-team-score-panel">
                <div className="league-team-score-label">
                  {team.currentMatchup?.weekNumber ? `Week ${team.currentMatchup.weekNumber} Score` : 'Points For'}
                </div>
                <div className="league-team-score-value">
                  {Number(team.currentMatchup?.points ?? team.pointsFor ?? 0).toFixed(1)}
                </div>
              </div>
              <div className="league-team-score-panel">
                <div className="league-team-score-label">Record</div>
                <div className="league-team-score-value">{formatRecord(team.wins, team.losses, team.ties)}</div>
              </div>
              <div className="league-team-score-panel">
                <div className="league-team-score-label">PF / PA</div>
                <div className="league-team-score-value">{Number(team.pointsFor || 0).toFixed(1)} / {Number(team.pointsAgainst || 0).toFixed(1)}</div>
              </div>
            </>
          ) : (
            <div className="league-team-score-panel">
              <div className="league-team-score-label">Team Score</div>
              <div className="league-team-score-value">{Number(team.teamScore || 0).toFixed(1)}</div>
            </div>
          )}
        </div>

        <div className="league-team-athlete-list">
          <div className="league-team-athlete-row league-team-owner-bonus-row">
            <span className="league-team-athlete-name">
              {isHeadToHeadMode && team.currentMatchup?.weekNumber
                ? `Week ${team.currentMatchup.weekNumber} Owner Bonus`
                : 'Owner Bonus'}
            </span>
            <span className="league-team-athlete-score">+{Number(team.ownerBonusPoints || 0).toFixed(1)} pts</span>
          </div>
          {(team.athletes || []).length ? (
            team.athletes.map((athleteEntry) => (
              <div key={athleteEntry.id} className="league-team-athlete-row">
                <span className="league-team-athlete-name">{athleteEntry.name}</span>
                <span className="league-team-athlete-score">{Number(athleteEntry.points || 0).toFixed(1)} {isHeadToHeadMode ? 'season pts' : 'pts'}</span>
              </div>
            ))
          ) : (
            <div className="league-team-empty">No athletes on this team yet.</div>
          )}
        </div>
      </div>
    );
  };

  const handleAddLeague = async (e) => {
    e.preventDefault();
    try {
      if (!effectiveAthleteId) {
        toast.error('Athlete ID not found. Please sign in again.');
        return;
      }

      const newLeague = await addLeague({
        name: leagueName,
        description: leagueDescription,
        admin: effectiveAthleteId,
        isPrivate,
      }).unwrap();

      toast.success('League created successfully.');
      navigate(`/leagues/${newLeague._id}`);
    } catch (error) {
      console.error('Failed to create league', error);
      toast.error(error?.data?.message || 'Failed to create league');
    }
  };

  const handleJoinLeague = async (nextLeagueId) => {
    try {
      if (!effectiveAthleteId) {
        toast.error('Athlete ID not found. Please sign in again.');
        return;
      }

      await joinLeague({
        leagueId: nextLeagueId,
        privateJoinToken: nextLeagueId === leagueId ? privateJoinToken : '',
      }).unwrap();

      toast.success('Successfully joined league.');
      navigate(`/leagues/${nextLeagueId}`);
    } catch (error) {
      console.error('Failed to join league', error);
      toast.error(error?.data?.message || 'Failed to join league');
    }
  };

  const handleShowCreateLeagueForm = () => {
    setShowCreateLeagueForm(true);
    createLeagueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleHideCreateLeagueForm = () => {
    setShowCreateLeagueForm(false);
    setLeagueName('');
    setLeagueDescription('');
    setIsPrivate(true);
  };

  const isLeagueAdmin = (userLeague?.admin?._id || userLeague?.admin)?.toString() === effectiveAthleteId?.toString();
  const hasValidPrivateShareLink = Boolean(userLeague?.isPrivate && privateJoinToken);
  const switchableLeagues = (allLeagues || []).filter((league) => {
    const leagueIdString = String(league?._id);
    const isMemberLeague = joinedLeagueIds.has(leagueIdString);
    const isManagedLeague = String(league?.admin?._id || league?.admin) === String(effectiveAthleteId);
    const isPrivateAccessibleLeague = Boolean(league?.isPrivate);

    return isMemberLeague || isManagedLeague || isPrivateAccessibleLeague;
  });
  const activeSwitchableLeagues = switchableLeagues.filter((league) => !isPastLeague(league));
  const isViewingJoinedLeague = leagueId ? joinedLeagueIds.has(String(leagueId)) : false;
  const seasonOptions = useMemo(
    () => (Array.isArray(leagueSeasons?.seasons) ? leagueSeasons.seasons : []),
    [leagueSeasons?.seasons]
  );
  const upcomingSeason = seasonOptions.find((season) => season.status === 'upcoming');
  const activeSeason = seasonOptions.find((season) => season.status === 'active');
  const completedSeasons = seasonOptions.filter((season) => season.status === 'completed');
  const latestCompletedSeason = completedSeasons
    .slice()
    .sort((left, right) => new Date(right.endDate).getTime() - new Date(left.endDate).getTime())[0] || null;
  const existingDraftStartTime = existingDraft?.startTime ? new Date(existingDraft.startTime) : null;
  const hasUpcomingDraftDate = Boolean(
    existingDraftStartTime &&
    !Number.isNaN(existingDraftStartTime.getTime()) &&
    existingDraftStartTime > new Date() &&
    existingDraft?.status === 'scheduled'
  );
  const shouldShowUpcomingDraft = Boolean(
    existingDraftStartTime &&
    !Number.isNaN(existingDraftStartTime.getTime()) &&
    existingDraftStartTime > new Date()
  );
  const activeSeasonEndDate = activeSeason?.endDate ? new Date(activeSeason.endDate) : null;
  const matchupSeason = activeSeason || latestCompletedSeason || upcomingSeason || null;
  const weeklyMatchupDateRanges = useMemo(
    () => buildWeekDateRanges(matchupSeason?.startDate, userLeague?.totalMatchupWeeks || 12),
    [matchupSeason?.startDate, userLeague?.totalMatchupWeeks]
  );
  const millisecondsUntilSeasonEnd =
    activeSeasonEndDate && !Number.isNaN(activeSeasonEndDate.getTime())
      ? activeSeasonEndDate.getTime() - Date.now()
      : null;
  const shouldPromptNextSeasonSetup = Boolean(
    isLeagueAdmin &&
    activeSeason &&
    millisecondsUntilSeasonEnd != null &&
    millisecondsUntilSeasonEnd >= 0 &&
    millisecondsUntilSeasonEnd <= 28 * 24 * 60 * 60 * 1000 &&
    !upcomingSeason
  );
  const shouldShowSeasonEndedSetupAlert = Boolean(
    latestCompletedSeason &&
    !activeSeason &&
    !upcomingSeason &&
    !hasUpcomingDraftDate
  );
  const shouldShowUpcomingSeasonDraftNotice = Boolean(!activeSeason && hasUpcomingDraftDate);

  useEffect(() => {
    if (!seasonOptions.length) {
      if (selectedSeasonId) {
        setSelectedSeasonId('');
      }
      return;
    }

    const hasSelectedSeason = seasonOptions.some((season) => String(season.id) === String(selectedSeasonId));
    if (hasSelectedSeason) {
      return;
    }

    const defaultSeason = activeSeason || upcomingSeason || latestCompletedSeason || seasonOptions[0];
    setSelectedSeasonId(defaultSeason?.id ? String(defaultSeason.id) : '');
  }, [activeSeason, latestCompletedSeason, selectedSeasonId, seasonOptions, upcomingSeason]);

  const selectedSeason = seasonOptions.find((season) => String(season.id) === String(selectedSeasonId)) || null;
  const isViewingCompletedSeason = selectedSeason?.status === 'completed';
  const { data: selectedSeasonLeaderboard, isLoading: isLoadingSelectedSeasonLeaderboard } = useGetSeasonLeaderboardQuery(selectedSeasonId, {
    skip: !selectedSeasonId || !isViewingCompletedSeason,
  });

  const renderCreateLeagueCard = ({ dismissible = false } = {}) => (
    <div className="modern-card" ref={createLeagueRef}>
      <div className="modern-card-header">
        <div>
          <h2 className="modern-card-title"><Icon icon={Rocket} size={18} className="me-2 align-middle" /> Create a League</h2>
          <p className="modern-card-subtitle">Start a league and invite other runners to compete.</p>
        </div>
        {dismissible ? (
          <button type="button" className="btn btn-outline-modern btn-sm-modern" onClick={handleHideCreateLeagueForm}>
            Close
          </button>
        ) : null}
      </div>

      <Form onSubmit={handleAddLeague}>
        <div className="grid-modern grid-cols-2 mb-4">
          <div className="form-group-modern">
            <label htmlFor="leagueName" className="form-label-modern">League Name</label>
            <input
              id="leagueName"
              type="text"
              className="form-input-modern"
              value={leagueName}
              onChange={(e) => setLeagueName(e.target.value)}
              required
            />
          </div>
          <div className="form-group-modern">
            <label htmlFor="leagueDescription" className="form-label-modern">Description</label>
            <input
              id="leagueDescription"
              type="text"
              className="form-input-modern"
              value={leagueDescription}
              onChange={(e) => setLeagueDescription(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-group-modern mb-4">
          <div className="form-check">
            <input
              id="isPrivate"
              type="checkbox"
              className="form-check-input"
              checked={!isPrivate}
              onChange={(e) => setIsPrivate(!e.target.checked)}
            />
            <label htmlFor="isPrivate" className="form-check-label">
              Make this league public
            </label>
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <button type="submit" className="btn btn-primary-modern" disabled={isAdding}>
            {isAdding ? 'Creating...' : 'Create League'}
          </button>
          {dismissible ? (
            <button type="button" className="btn btn-outline-modern" onClick={handleHideCreateLeagueForm} disabled={isAdding}>
              Cancel
            </button>
          ) : null}
        </div>
      </Form>
    </div>
  );

  const isLoading =
    isLoadingAthlete ||
    isLoadingAllLeagues ||
    isLoadingUserLeague ||
    isLoadingLeagueTeams;

  if (isLoading) {
    return <Loader />;
  }

  if (!leagueId) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="fade-in">
            <h1 className="text-primary mb-5 text-center">Join or Create a League</h1>

            <div className="modern-card mb-5" ref={availableLeaguesRef}>
              <div className="modern-card-header">
                <h2 className="modern-card-title"><Icon icon={Trophy} size={18} className="me-2 align-middle" /> Available Leagues</h2>
                <p className="modern-card-subtitle">The fastest way into competition.</p>
              </div>

              {pastMemberLeagues.length > 0 && (
                <div className="modern-card mb-4 border-secondary">
                  <div className="modern-card-header compact-header">
                    <h3 className="modern-card-title"><Icon icon={CalendarDays} size={18} className="me-2 align-middle" /> Past Leagues</h3>
                    <p className="modern-card-subtitle">Reopen leagues you competed in after their season has ended.</p>
                  </div>
                  <div className="p-3 d-flex flex-column gap-3">
                    {pastMemberLeagues.map((league) => (
                      <div
                        key={league._id}
                        className="d-flex justify-content-between align-items-center p-3 rounded border"
                        style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
                      >
                        <div>
                          <div className="d-flex align-items-center mb-2 gap-2 flex-wrap">
                            <h4 className="text-primary mb-0">{league.name}</h4>
                            <Badge bg="secondary">Past League</Badge>
                          </div>
                          <p className="text-secondary mb-1">{league.description}</p>
                          <small className="text-secondary">
                            {league.seasonSummary?.latestCompletedSeason
                              ? `Season ${league.seasonSummary.latestCompletedSeason.seasonNumber} ended ${formatDate(league.seasonSummary.latestCompletedSeason.endDate)}`
                              : 'Completed league'}
                          </small>
                        </div>
                        <button className="btn btn-outline-modern" onClick={() => navigate(`/leagues/${league._id}`)}>
                          View Past League
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {visibleAvailableLeagues.length ? (
                <div className="grid-modern grid-cols-1 gap-4">
                  {visibleAvailableLeagues.map((league) => (
                    <div
                      key={league._id}
                      className="d-flex justify-content-between align-items-center p-4 rounded border"
                      style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
                    >
                      <div>
                        <div className="d-flex align-items-center mb-2 gap-2 flex-wrap">
                          <h4 className="text-primary mb-0">{league.name}</h4>
                          <Badge bg={league.isPrivate ? 'secondary' : 'success'}>
                            {league.isPrivate ? 'Private' : 'Public'}
                          </Badge>
                        </div>
                        <p className="text-secondary mb-1">{league.description}</p>
                        <small className="text-secondary">
                          {(league.athletes || []).length} members
                          {' · '}
                          Draft: {draftsByLeague[league._id]?.startTime ? new Date(draftsByLeague[league._id].startTime).toLocaleString() : 'Not scheduled'}
                        </small>
                      </div>
                      {joinedLeagueIds.has(String(league._id)) ? (
                        <button className="btn btn-outline-modern" onClick={() => navigate(`/leagues/${league._id}`)}>
                          Open League
                        </button>
                      ) : (
                        <button className="btn btn-primary-modern" onClick={() => handleJoinLeague(league._id)}>
                          Join League
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <p className="text-secondary">No leagues are available yet. Create the first one.</p>
                </div>
              )}
            </div>

            {renderCreateLeagueCard()}
          </div>
        </div>
      </div>
    );
  }

  const handleOpenLeagueManage = () => {
    navigate(`/leagues/${leagueId}/manage`);
  };

  const selectedSeasonLabel = (season) => {
    if (!season) return '';

    const statusLabel = season.status === 'completed'
      ? 'Completed'
      : season.status === 'active'
        ? 'Active'
        : 'Upcoming';

    return `Season ${season.seasonNumber} · ${statusLabel}`;
  };

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="fade-in">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-5">
            <div>
              <h1 className="text-primary mb-2">LEAGUE: {userLeague?.name}</h1>
              <p className="text-secondary mb-0">Standings, matchups, teams, and draft</p>
            </div>

            {isLeagueAdmin && (
              <div className="d-flex gap-2 flex-wrap">
                <button className="btn btn-primary-modern" onClick={handleOpenLeagueManage}>
                  Manage League
                </button>
              </div>
            )}
          </div>

          {activeSwitchableLeagues.length ? (
            <div className="modern-card mb-4">
              <div className="modern-card-header compact-header">
                <div>
                  <h2 className="modern-card-title"><Icon icon={Info} size={18} className="me-2 align-middle" /> Your Leagues</h2>
                  <p className="modern-card-subtitle">Switch between leagues you manage, belong to, or have been invited to.</p>
                </div>
              </div>
              <div className="p-3 d-flex flex-wrap gap-2">
                {activeSwitchableLeagues.map((league) => {
                  const isCurrentLeague = String(league._id) === String(leagueId) && joinedLeagueIds.has(String(league._id));
                  const isJoinedLeague = joinedLeagueIds.has(String(league._id));

                  return (
                    <button
                      key={league._id}
                      type="button"
                      className={`btn ${isCurrentLeague ? 'btn-primary-modern' : 'btn-outline-modern'}`}
                      onClick={() => (isJoinedLeague ? navigate(`/leagues/${league._id}`) : handleJoinLeague(league._id))}
                    >
                      {isCurrentLeague ? `${league.name} (Current)` : isJoinedLeague ? `Open ${league.name}` : `Join ${league.name}`}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`btn ${showCreateLeagueForm ? 'btn-primary-modern' : 'btn-outline-modern'}`}
                  onClick={handleShowCreateLeagueForm}
                >
                  Create League
                </button>
              </div>
            </div>
          ) : null}

          {showCreateLeagueForm ? (
            <div className="mb-4">
              {renderCreateLeagueCard({ dismissible: true })}
            </div>
          ) : null}

          {pastMemberLeagues.length ? (
            <div className="modern-card mb-4">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={CalendarDays} size={18} className="me-2 align-middle" /> Past Leagues</h2>
                <p className="modern-card-subtitle">Open leagues you were part of after the season wrapped up.</p>
              </div>
              <div className="p-3 d-flex flex-wrap gap-2">
                {pastMemberLeagues.map((league) => {
                  const isCurrentLeague = String(league._id) === String(leagueId);
                  const latestCompletedSeasonForLeague = league?.seasonSummary?.latestCompletedSeason;

                  return (
                    <button
                      key={league._id}
                      type="button"
                      className={`btn ${isCurrentLeague ? 'btn-primary-modern' : 'btn-outline-modern'}`}
                      onClick={() => navigate(`/leagues/${league._id}`)}
                      title={latestCompletedSeasonForLeague ? `Season ${latestCompletedSeasonForLeague.seasonNumber} ended ${formatDate(latestCompletedSeasonForLeague.endDate)}` : 'Open past league'}
                    >
                      {isCurrentLeague ? `${league.name} (Viewing)` : `Open ${league.name}`}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {leagueId && !isViewingJoinedLeague ? (
            <div className="modern-card mb-4 border-warning">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-3">
                <div>
                  <h3 className="fs-5 mb-1">You have not joined this league yet</h3>
                  <p className="mb-0 text-secondary">Join this league to create a team here and participate with the same account.</p>
                </div>
                <button type="button" className="btn btn-warning" onClick={() => handleJoinLeague(leagueId)}>
                  Join This League
                </button>
              </div>
            </div>
          ) : null}

          {(activeSeason || shouldShowUpcomingSeasonDraftNotice || shouldShowSeasonEndedSetupAlert) && (
            <div className="modern-card mb-4">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={CalendarDays} size={18} className="me-2 align-middle" /> Season Schedule</h2>
                <p className="modern-card-subtitle">Current season timing, upcoming draft details, and next-season setup status.</p>
              </div>
              <div className="p-3 d-flex flex-column gap-3">
                {seasonOptions.length > 0 && (
                  <div className="form-group-modern mb-0">
                    <label htmlFor="seasonSelector" className="form-label-modern">View season</label>
                    <Form.Select
                      id="seasonSelector"
                      value={selectedSeasonId}
                      onChange={(e) => setSelectedSeasonId(e.target.value)}
                    >
                      {seasonOptions.map((season) => (
                        <option key={season.id} value={season.id}>
                          {selectedSeasonLabel(season)}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                )}

                {activeSeason && (
                  <Alert variant="info" className="mb-0">
                    <div className="d-flex flex-column gap-2">
                      <div>
                        <Alert.Heading className="fs-6 mb-1">Current season window</Alert.Heading>
                        <p className="mb-0">
                          Season {activeSeason.seasonNumber} runs from <strong>{formatDate(activeSeason.startDate)}</strong> to <strong>{formatDate(activeSeason.endDate)}</strong>.
                        </p>
                      </div>
                      {shouldPromptNextSeasonSetup && (
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <span>Less than four weeks remain in the current season. Start the next season setup now, including the next draft date.</span>
                          <button type="button" className="btn btn-outline-modern btn-sm-modern" onClick={handleOpenLeagueManage}>
                            Open League Management
                          </button>
                        </div>
                      )}
                    </div>
                  </Alert>
                )}

                {selectedSeason && isViewingCompletedSeason && (
                  <Alert variant="secondary" className="mb-0">
                    <div className="d-flex flex-column gap-3">
                      <div>
                        <Alert.Heading className="fs-6 mb-1">Viewing Season {selectedSeason.seasonNumber}</Alert.Heading>
                        <p className="mb-0">
                          This season ran from <strong>{formatDate(selectedSeason.startDate)}</strong> to <strong>{formatDate(selectedSeason.endDate)}</strong>.
                        </p>
                      </div>

                      {(selectedSeason.champions?.first || selectedSeason.champions?.second || selectedSeason.champions?.third) && (
                        <div className="d-flex flex-wrap gap-2">
                          {selectedSeason.champions?.first?.name ? <Badge bg="warning" text="dark">1st: {selectedSeason.champions.first.name}</Badge> : null}
                          {selectedSeason.champions?.second?.name ? <Badge bg="secondary">2nd: {selectedSeason.champions.second.name}</Badge> : null}
                          {selectedSeason.champions?.third?.name ? <Badge bg="dark">3rd: {selectedSeason.champions.third.name}</Badge> : null}
                        </div>
                      )}

                      {isLoadingSelectedSeasonLeaderboard ? (
                        <div className="text-secondary">Loading season standings...</div>
                      ) : selectedSeasonLeaderboard?.leaderboard?.length ? (
                        <div className="table-responsive">
                          <Table hover className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Team</th>
                                <th className="text-end">Points</th>
                                <th className="text-end">Miles</th>
                                <th className="text-end">Activities</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedSeasonLeaderboard.leaderboard.map((entry) => (
                                <tr key={entry.teamId}>
                                  <td>{entry.rank}</td>
                                  <td>{entry.teamName}</td>
                                  <td className="text-end">{Number(entry.totalPoints || 0).toFixed(1)}</td>
                                  <td className="text-end">{Number(entry.totalMiles || 0).toFixed(1)}</td>
                                  <td className="text-end">{entry.totalActivities || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <p className="mb-0 text-secondary">No archived standings are available for this season yet.</p>
                      )}
                    </div>
                  </Alert>
                )}

                {shouldShowUpcomingSeasonDraftNotice && (
                  <Alert variant="warning" className="mb-0">
                    <Alert.Heading className="fs-6 mb-1">Upcoming draft</Alert.Heading>
                    <p className="mb-0">
                      The next league draft is scheduled for <strong>{formatDateTime(existingDraft.startTime)}</strong>.
                      {upcomingSeason ? ` The upcoming season begins on ${formatDate(upcomingSeason.startDate)}.` : ''}
                    </p>
                  </Alert>
                )}

                {shouldShowSeasonEndedSetupAlert && (
                  <Alert variant="warning" className="mb-0">
                    <div className="d-flex flex-column gap-2">
                      <div>
                        <Alert.Heading className="fs-6 mb-1">Next season is not scheduled yet</Alert.Heading>
                        <p className="mb-0">
                          Season {latestCompletedSeason.seasonNumber} ended on <strong>{formatDate(latestCompletedSeason.endDate)}</strong>, and no next-season draft or start date is scheduled yet.
                        </p>
                      </div>
                      {isLeagueAdmin ? (
                        <div className="d-flex flex-wrap gap-2">
                          <button type="button" className="btn btn-outline-modern btn-sm-modern" onClick={handleOpenLeagueManage}>
                            Open League Management
                          </button>
                        </div>
                      ) : (
                        <p className="mb-0 text-secondary">Your league admin still needs to schedule the next draft and season dates.</p>
                      )}
                    </div>
                  </Alert>
                )}
              </div>
            </div>
          )}

          {!currentLeagueUserTeam && (
            <div className="modern-card mb-4 border-warning">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0 me-3">
                  <Icon icon={AlertTriangle} size={24} className="text-warning" />
                </div>
                <div className="flex-grow-1">
                  <h3 className="fs-5 mb-2">You need a team to compete</h3>
                  <p className="mb-3">Create your team so you can draft athletes and show up in the standings.</p>
                  <button className="btn btn-warning" onClick={() => navigate(leagueId ? `/team?leagueId=${encodeURIComponent(leagueId)}` : '/team')}>
                    Create Team
                  </button>
                </div>
              </div>
            </div>
          )}
          {shouldShowUpcomingDraft && (
            <UpcomingDraft
              draft={existingDraft}
              isAdmin={false}
            />
          )}

          <div className="modern-card mb-4">
            <div className="modern-card-header compact-header">
              <h2 className="modern-card-title"><Icon icon={Info} size={18} className="me-2 align-middle" /> League Views</h2>
              <p className="modern-card-subtitle">Switch between standings, matchups, weekly trends, teams, and athlete rankings.</p>
            </div>
            <div className="px-3 pt-0 pb-3">
              <Nav variant="tabs" className="league-page-tabs flex-nowrap overflow-auto" activeKey={selectedLeagueTab} onSelect={(eventKey) => setSelectedLeagueTab(eventKey || 'standings')}>
                <Nav.Item>
                  <Nav.Link eventKey="standings">Standings</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="matchups">Matchups</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="weekly-team-points">Weekly Team Points</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="teams">Teams</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="athlete-standings">Athlete Standings</Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          </div>

          {selectedLeagueTab === 'standings' && (
            <>
              <div className="modern-card mb-4">
                <div className="modern-card-header compact-header">
                  <h2 className="modern-card-title"><Icon icon={Trophy} size={18} className="me-2 align-middle" /> League Standings</h2>
                  <p className="modern-card-subtitle">
                    Switch between division standings and the overall league table.
                  </p>
                </div>
                <div className="px-3 pt-0 pb-3">
                  <Nav variant="tabs" className="league-sub-tabs flex-nowrap overflow-auto" activeKey={selectedStandingsTab} onSelect={(eventKey) => setSelectedStandingsTab(eventKey || 'division')}>
                    <Nav.Item>
                      <Nav.Link eventKey="overall">Overall Standings</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="division">Division Standings</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>

                {selectedStandingsTab === 'overall' ? (
                  isHeadToHeadMode ? (
                    <LeagueStandings
                      mode="head_to_head"
                      standings={userLeague?.headToHeadStandings || []}
                      resultsByWeek={userLeague?.resultsByWeek || {}}
                    />
                  ) : (
                    <LeagueStandings teams={leagueTeamCards} mode="cumulative" />
                  )
                ) : (
                  <div className="league-division-grid p-3 pt-0">
                    {divisionStandings.map((division) => (
                      <div key={division.key} className="league-division-card">
                        <div className="league-division-header">
                          <h3 className="league-division-title">{division.label}</h3>
                          <Badge bg="secondary">{division.teams.length} teams</Badge>
                        </div>
                        <div className="league-division-table-wrap">
                          <table className="league-division-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Team</th>
                                {isHeadToHeadMode ? (
                                  <>
                                    <th className="text-end">Record</th>
                                    <th className="text-end">PF</th>
                                  </>
                                ) : (
                                  <>
                                    <th className="text-end">Multiplier</th>
                                    <th className="text-end">Base Pts</th>
                                  </>
                                )}
                                <th className="text-end">{isHeadToHeadMode ? 'Pts For' : 'Total'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {division.teams.length ? (
                                division.teams.map((team) => {
                                  const sourceTeam = teamsById[String(team.id)];
                                  return (
                                    <tr key={team.id}>
                                      <td>{team.rank}</td>
                                      <td>
                                        <div className="league-division-team-name">{team.name}</div>
                                      </td>
                                      {isHeadToHeadMode ? (
                                        <>
                                          <td className="text-end">{formatRecord(sourceTeam?.wins, sourceTeam?.losses, sourceTeam?.ties)}</td>
                                          <td className="text-end">{Number(sourceTeam?.pointsFor || 0).toFixed(1)}</td>
                                        </>
                                      ) : (
                                        <>
                                          <td className="text-end">{team.multiplier.toFixed(3)}x</td>
                                          <td className="text-end">{team.basePoints.toFixed(1)}</td>
                                        </>
                                      )}
                                      <td className="text-end">{team.total.toFixed(1)}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={5} className="text-center text-secondary">No teams yet</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {selectedLeagueTab === 'matchups' && (
            isHeadToHeadMode ? (
              <WeeklyMatchups
                weeks={userLeague?.totalMatchupWeeks || 12}
                matchupsByWeek={userLeague?.matchupsByWeek || {}}
                teamsById={teamsById}
                weekDateRanges={weeklyMatchupDateRanges}
              />
            ) : (
              <div className="modern-card mb-3">
                <div className="modern-card-header compact-header">
                  <h2 className="modern-card-title"><Icon icon={Users} size={18} className="me-2 align-middle" /> Matchups</h2>
                  <p className="modern-card-subtitle">Weekly matchups are only available in head-to-head leagues.</p>
                </div>
              </div>
            )
          )}

          {selectedLeagueTab === 'weekly-team-points' && (
            <TeamStandingsChart teams={leagueTeamCards} isSeasonInactive={noActiveSeasonForLeaderboard} mode={isHeadToHeadMode ? 'head_to_head' : 'cumulative'} />
          )}

          {selectedLeagueTab === 'teams' && (
            <div className="modern-card mb-3">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={Users} size={18} className="me-2 align-middle" /> Teams</h2>
                <p className="modern-card-subtitle">
                  {isHeadToHeadMode
                    ? 'Pick a team to inspect its featured matchup, record, and roster scoring.'
                    : 'Pick a team to inspect its score breakdown and the athletes driving it.'}
                </p>
              </div>

              {leagueTeamCards?.length ? (
                <div className="p-3 pt-0 d-flex flex-column gap-3">
                  <div className="league-team-selector-row">
                    <div className="form-group-modern mb-0 league-team-selector-control">
                      <label htmlFor="leagueTeamSelector" className="form-label-modern">Select team</label>
                      <Form.Select id="leagueTeamSelector" value={selectedTeamId} onChange={(e) => setSelectedTeamId(e.target.value)}>
                        {leagueTeamCards.map((team) => (
                          <option key={team._id} value={team._id}>{team.name}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                  {renderSelectedTeamCard(selectedLeagueTeam)}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-secondary">No teams are in this league yet.</p>
                </div>
              )}
              {noActiveSeasonForLeaderboard && leagueTeamCards?.length ? (
                <div className="px-3 pb-3">
                  <Alert variant="info" className="mb-0">
                    {isHeadToHeadMode
                      ? 'No active season is running right now, so matchup points and weekly charts stay at 0.0 until the season starts.'
                      : 'No active season is running right now, so team and athlete scores are shown as 0.0 until the season starts.'}
                  </Alert>
                </div>
              ) : null}
            </div>
          )}

          {selectedLeagueTab === 'athlete-standings' && (
            <div className="modern-card mb-3">
              <div className="modern-card-header compact-header">
                <h2 className="modern-card-title"><Icon icon={UserCircle} size={18} className="me-2 align-middle" /> Athlete Standings</h2>
                <p className="modern-card-subtitle">Draft rank, current multiplier, season mileage, and current-week mileage for every league athlete.</p>
              </div>
              {sortedAthleteStandingsRows.length ? (
                <div className="table-responsive p-3 pt-0">
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleAthleteStandingsSort('rank')}>Draft Rank{getSortIndicator('rank')}</button></th>
                        <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleAthleteStandingsSort('name')}>Athlete{getSortIndicator('name')}</button></th>
                        <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleAthleteStandingsSort('multiplier')}>Current Multiplier{getSortIndicator('multiplier')}</button></th>
                        <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleAthleteStandingsSort('multiplierCap')}>Ceiling{getSortIndicator('multiplierCap')}</button></th>
                        <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleAthleteStandingsSort('seasonMiles')}>Season Miles{getSortIndicator('seasonMiles')}</button></th>
                        <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleAthleteStandingsSort('currentWeekMiles')}>{currentLeagueWeekNumber ? `Week ${currentLeagueWeekNumber}` : 'Week'}{getSortIndicator('currentWeekMiles')}</button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAthleteStandingsRows.map((leagueAthlete) => (
                        <tr key={leagueAthlete.id}>
                          <td>{leagueAthlete.rank || 'Unranked'}</td>
                          <td>{leagueAthlete.name}</td>
                          <td>{leagueAthlete.multiplier.toFixed(3)}x</td>
                          <td>{leagueAthlete.multiplierCap.toFixed(3)}x</td>
                          <td>{leagueAthlete.seasonMiles.toFixed(1)} mi</td>
                          <td>{leagueAthlete.currentWeekMiles.toFixed(1)} mi</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-secondary mb-0">No athletes are in this league yet.</p>
                </div>
              )}
            </div>
          )}

          {isLeagueAdmin && (
            <div className="modern-card mb-4">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-3">
                <div>
                  <h3 className="fs-5 mb-1">League owner controls moved out of this hub</h3>
                  <p className="mb-0 text-secondary">Draft setup, invites, and athlete assignment now live in a dedicated management space so this page stays focused on competition.</p>
                </div>
                <button type="button" className="btn btn-primary-modern" onClick={handleOpenLeagueManage}>
                  Open League Management
                </button>
              </div>
            </div>
          )}

          <div className="modern-card mb-4">
            <div className="modern-card-header compact-header">
              <h2 className="modern-card-title"><Icon icon={Info} size={18} className="me-2 align-middle" /> League Summary</h2>
              <p className="modern-card-subtitle">Only the rules and context that matter during competition.</p>
            </div>
            <div className="p-3">
              <p className="mb-3">{userLeague?.description || 'No description available.'}</p>
              <div className="grid-modern grid-cols-2">
                <div>
                  <h3 className="fs-6 text-primary mb-2">How standings work</h3>
                  <p className="mb-0">
                    {isHeadToHeadMode
                      ? 'Each week uses the same team scoring rules, but standings are based on weekly wins and losses instead of one cumulative ladder. Current matchup scores come from the current week only.'
                      : 'Teams rise based on starter points plus an owner mileage bonus. That bonus is the owner\'s season miles multiplied by a fixed draft-rank multiplier, so higher-ranked owners get less extra help and lower-ranked owners get more.'}
                  </p>
                </div>
                <div>
                  <h3 className="fs-6 text-primary mb-2">Divisions</h3>
                  <p className="mb-0">Leagues with fewer than 13 teams use East and West. Larger leagues also use North and South.</p>
                </div>
                <div>
                  <h3 className="fs-6 text-primary mb-2">Draft setup</h3>
                  <p className="mb-0">Snake draft, 60 seconds per pick, scheduled by the league admin.</p>
                </div>
                <div>
                  <h3 className="fs-6 text-primary mb-2">Membership</h3>
                  <p className="mb-0">{userLeague?.isPrivate ? (hasValidPrivateShareLink ? 'Private league access granted through a shared join link.' : 'Private league with invites required.') : 'Public league open to all users.'}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LeaguesScreen;
